import type { Channel, CustomRole, TaskStatus, Team, TeamMember } from '@nexo/types';
import { createContext, useContext } from 'react';

/** Draft used to open the task panel, either to edit a task or to create one from context. */
export type TaskDraft =
  | { mode: 'edit'; taskId: string }
  | {
      mode: 'create';
      title?: string;
      channelId?: string | null;
      sourceMessageId?: string | null;
      assigneeIds?: string[];
      status?: TaskStatus;
      /** Plan it in this sprint (none: the backlog). */
      sprintId?: string | null;
      /** Board column it starts in. */
      columnId?: string;
    };

/** Contextual panels on the right-hand side of the workspace. */
export type RightPanel = 'members' | 'tasks' | 'files' | 'integrations' | 'pins' | 'thread';

export type TeamDialog = 'channel' | 'voice' | 'dm' | 'members' | 'settings';

export interface TeamContextValue {
  team: Team;
  members: TeamMember[];
  /** Custom roles of the team (labels like «Diseño», mentionable as @Diseño). */
  roles: CustomRole[];
  roleById: (id: string) => CustomRole | undefined;
  channels: Channel[];
  /** Online user ids, or null if presence is unavailable. */
  online: Set<string> | null;
  memberById: (id: string) => TeamMember | undefined;
  openTask: (draft: TaskDraft) => void;
  openDialog: (dialog: TeamDialog) => void;
  openSidebar: () => void;
  rightPanel: RightPanel | null;
  setRightPanel: (panel: RightPanel | null) => void;
  /** Message whose thread is open in the right panel. */
  threadId: string | null;
  openThread: (messageId: string) => void;
}

export const TeamContext = createContext<TeamContextValue | null>(null);

export function useTeam(): TeamContextValue {
  const context = useContext(TeamContext);
  if (!context) throw new Error('useTeam must be used inside <TeamShell>');
  return context;
}
